/* towsons-free-delivery.js — free delivery as a little game.
 *
 * Philip, 29 Sep 2026: "could we add a celebration style graphics when the
 * customer hits the target, I almost want it to feel like a game". The card
 * (snippets/towsons-free-delivery.liquid — the drawer and the cart page) is a
 * road: the bar, a van riding it, a flag at the finish. Liquid draws it afresh
 * on every change — the drawer swaps its HTML after an add, the cart page
 * reloads on a quantity change — so this remembers where the van was (per
 * tab, in sessionStorage) and, whenever a card appears:
 *
 *   - drives the van and the bar on from where they were;
 *   - when the basket has just reached free delivery, throws a party: the
 *     van reaches the flag, the flag waves, the card pops, and confetti in
 *     the shop's colours comes down over the page.
 *
 * Once the basket is there, the road has done its job — "You've unlocked
 * FREE delivery" says it — so an arrived card has no road (Philip, 29 Sep
 * 2026: "do we need the progress line if they have completed the goal"),
 * except while the finish is run: --racing keeps it out until the flag has
 * waved, then it folds away.
 *
 * The show waits until the shopper can see it — a drawer still closed keeps
 * it owed, even across another redraw — plays once per crossing (dropping
 * back under and crossing again earns another), and never on first sight of
 * a basket that was already there. Nothing here changes the card's size: the
 * drawer measures itself from its HTML the moment it is drawn (Warehouse's
 * mini-cart). Anyone who asked for less movement gets the new state without
 * the show.
 */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.TowsonsFreeDelivery = api;
})(typeof window !== 'undefined' ? window : null, function (win) {
  'use strict';

  var KEY = 'towsons-freeship';
  // The shop's yellow first, then its green, the logo's orange and its ink.
  var COLOURS = ['#ffd214', '#ffe680', '#006129', '#34c759', '#e8772e', '#142129'];

  function read() {
    try {
      var v = JSON.parse(win.sessionStorage.getItem(KEY) || 'null');
      return v && typeof v.pct === 'number' ? v : null;
    } catch (e) { return null; }
  }
  function write(v) {
    try { win.sessionStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* no memory here: no show, nothing lost */ }
  }

  // A crossing already cheered on the product page: the quantity took the
  // basket-to-be over the line there, confetti and all (towsons-delivery.js,
  // Philip, 29 Sep 2026). When that basket gets there, its card runs the
  // finish without a second shower. Fresh for ten minutes; dropping back
  // under on the product page takes it back.
  var CHEER = 'towsons-freeship-cheer';
  var CHEER_MS = 10 * 60 * 1000;
  function cheer(threshold) {
    try { win.sessionStorage.setItem(CHEER, JSON.stringify({ threshold: Number(threshold) || 0, at: Date.now() })); } catch (e) { /* the drawer throws its own */ }
  }
  function uncheer() {
    try { win.sessionStorage.removeItem(CHEER); } catch (e) { /* nothing to take back */ }
  }
  function cheered(threshold) {
    try {
      var c = JSON.parse(win.sessionStorage.getItem(CHEER) || 'null');
      return !!(c && c.threshold === threshold && Date.now() - c.at < CHEER_MS);
    } catch (e) { return false; }
  }

  // What the card says about itself (the snippet writes these).
  function stateOf(card) {
    var pct = Number(card.getAttribute('data-tfd-pct'));
    return {
      pct: isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0,
      done: card.getAttribute('data-tfd-done') === 'true',
      threshold: Number(card.getAttribute('data-tfd-threshold')) || 0,
    };
  }

  // Where the van drives from, and whether this card owes a party. A first
  // sight, or a new free amount, is where the van already is: no show.
  function plan(prev, now) {
    if (!prev || prev.threshold !== now.threshold) return { from: now.pct, party: false };
    return { from: prev.pct, party: !!now.done && (!prev.done || !!prev.owed) };
  }

  function quiet() {
    try { return !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }

  function place(card, pct) {
    var road = card.querySelector('.towsons-freeship__road');
    var fill = card.querySelector('.towsons-freeship__fill');
    if (road) road.style.setProperty('--tfd-pct', String(pct));
    if (fill) fill.style.width = pct + '%';
  }

  // Seen: on the cart page straight away; in the drawer once it is open.
  function whenSeen(card, fn) {
    var drawer = card.closest ? card.closest('.mini-cart') : null;
    if (!drawer) { win.setTimeout(fn, 200); return; }
    var open = function () { return drawer.getAttribute('aria-hidden') === 'false'; };
    if (open()) { win.setTimeout(fn, 250); return; }
    var mo = new win.MutationObserver(function () {
      if (!card.isConnected) { mo.disconnect(); return; }
      if (open()) { mo.disconnect(); win.setTimeout(fn, 300); }
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  function mount(card) {
    if (!card || card.__tfd) return;
    card.__tfd = true;
    var now = stateOf(card);
    var p = plan(read(), now);
    var show = !quiet();
    write({ pct: now.pct, done: now.done, threshold: now.threshold, owed: p.party && show });
    if (!show || (p.from === now.pct && !p.party)) return;
    if (p.party) card.classList.add('towsons-freeship--racing');   // the road stays out for the finish
    place(card, p.from);   // where it was, until the shopper can watch it move
    whenSeen(card, function () {
      if (!card.isConnected) return;
      void card.offsetWidth;   // commit where it starts
      win.requestAnimationFrame(function () { place(card, now.pct); });
      if (p.party) party(card);
    });
  }

  // The road folds away: transitions on from here, then the class off.
  function fold(card) {
    card.classList.add('towsons-freeship--ready');
    win.requestAnimationFrame(function () { card.classList.remove('towsons-freeship--racing'); });
  }

  // The finish. Once per crossing: the first card to be seen throws it; any
  // other card that was keeping its road out for it just folds.
  function party(card, force) {
    if (!force) {
      var s = read();
      if (!s || !s.owed) { fold(card); return; }
      s.owed = false;
      write(s);
    }
    // Cheered on the product page already: the finish, without the shower.
    var threshold = stateOf(card).threshold;
    var shower = !cheered(threshold);
    if (!shower) uncheer();
    card.classList.add('towsons-freeship--racing');
    card.classList.add('towsons-freeship--party');
    if (shower) {
      try { if (win.navigator && win.navigator.vibrate) win.navigator.vibrate([10, 50, 18]); } catch (e) { /* no buzz */ }
    }
    win.setTimeout(function () {
      if (!card.isConnected || !shower) return;
      var goal = card.querySelector('.towsons-freeship__goal') || card;
      var r = goal.getBoundingClientRect();
      confetti(r.left + r.width / 2, r.top + r.height / 2);
    }, 700);   // as the van reaches the flag
    win.setTimeout(function () { fold(card); }, 2300);   // the flag has waved: the road's job is done
    win.setTimeout(function () { card.classList.remove('towsons-freeship--party'); }, 3200);
  }

  // Confetti over the whole page, from the finish line, then gone. Paced by
  // the clock, not the frame, so a 120Hz phone does not fling it twice as fast.
  function confetti(x, y) {
    var doc = win.document;
    if (!doc || !doc.body) return null;
    var c = doc.createElement('canvas');
    var ctx = c.getContext ? c.getContext('2d') : null;
    if (!ctx) return null;
    var dpr = Math.min(win.devicePixelRatio || 1, 2);
    var W = win.innerWidth;
    var H = win.innerHeight;
    c.width = Math.round(W * dpr);
    c.height = Math.round(H * dpr);
    c.className = 'towsons-confetti';
    c.setAttribute('aria-hidden', 'true');
    c.style.cssText = 'position:fixed;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:2147483000';
    doc.body.appendChild(c);
    ctx.scale(dpr, dpr);
    var bits = [];
    for (var i = 0; i < 140; i++) {
      var a = -Math.PI / 2 + (Math.random() - 0.5) * 2.4;   // a fan, mostly upwards
      var v = 6 + Math.random() * 10;
      bits.push({
        x: x, y: y, vx: Math.cos(a) * v, vy: Math.sin(a) * v,
        w: 5 + Math.random() * 6, h: 8 + Math.random() * 8,
        r: Math.random() * 6.283, vr: (Math.random() - 0.5) * 0.35,
        c: COLOURS[i % COLOURS.length], k: i % 3,
      });
    }
    var start = null;
    var last = null;
    function frame(t) {
      if (start === null) { start = t; last = t; }
      var dt = Math.min(3, (t - last) / 16.67);
      last = t;
      var age = t - start;
      var fade = age < 1900 ? 1 : Math.max(0, 1 - (age - 1900) / 800);
      ctx.clearRect(0, 0, W, H);
      for (var j = 0; j < bits.length; j++) {
        var b = bits[j];
        var drag = Math.pow(0.985, dt);
        b.vx *= drag;
        b.vy = b.vy * drag + 0.3 * dt;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.r += b.vr * dt;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(b.x, b.y);
        ctx.rotate(b.r);
        ctx.fillStyle = b.c;
        if (b.k === 0) {
          // A paper strip, flipping as it falls.
          var flip = Math.abs(Math.cos(b.r * 1.7));
          ctx.fillRect(-b.w / 2, -b.h * flip / 2, b.w, Math.max(1, b.h * flip));
        } else if (b.k === 1) {
          ctx.beginPath(); ctx.arc(0, 0, b.w / 2.2, 0, 6.283); ctx.fill();
        } else {
          ctx.beginPath(); ctx.moveTo(0, -b.h / 2); ctx.lineTo(b.w / 2, b.h / 2); ctx.lineTo(-b.w / 2, b.h / 2); ctx.closePath(); ctx.fill();
        }
        ctx.restore();
      }
      if (age < 2700) win.requestAnimationFrame(frame);
      else if (c.parentNode) c.parentNode.removeChild(c);
    }
    win.requestAnimationFrame(frame);
    return c;
  }

  function scan(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.matches && node.matches('[data-towsons-freeship]')) mount(node);
    if (node.querySelectorAll) Array.prototype.forEach.call(node.querySelectorAll('[data-towsons-freeship]'), mount);
  }
  function boot() {
    var doc = win && win.document;
    if (!doc || boot.done) return;
    boot.done = true;
    scan(doc.documentElement);
    try {
      new win.MutationObserver(function (list) {
        list.forEach(function (m) { Array.prototype.forEach.call(m.addedNodes || [], scan); });
      }).observe(doc.documentElement, { childList: true, subtree: true });
    } catch (e) { /* an old browser: the card still works, without the show */ }
  }
  if (win && win.document) {
    if (win.document.readyState === 'loading') win.document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  return { KEY: KEY, CHEER: CHEER, plan: plan, stateOf: stateOf, mount: mount, party: party, confetti: confetti, boot: boot, cheer: cheer, uncheer: uncheer, cheered: cheered };
});
